import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal, Descriptions, Table, Typography, Tag, Space, Divider, Button, Alert, App, Dropdown } from 'antd';
import { FileTextOutlined, PrinterOutlined, UserOutlined, PhoneOutlined, EnvironmentOutlined, FileTextOutlined as NotesIcon, DollarOutlined, CreditCardOutlined, BankOutlined, ReloadOutlined, SafetyCertificateOutlined, SwapOutlined, EditOutlined, PlusCircleOutlined, MoreOutlined, DeleteOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { Order, OrderItem, OrderType, OrderPayment } from '../../../types/orders';
import SolicitarFacturaModal from '../../facturacion/SolicitarFacturaModal';
import FacturaDetalleModal from '../../facturacion/FacturaDetalleModal';
import ChangePaymentMethodModal from './ChangePaymentMethodModal';
import PaymentMethodChangesHistory from './PaymentMethodChangesHistory';
import { useTimbrarFacturaMutation } from '../../../services/facturacionApi';
import { useGetOrderQuery, useCancelOrderItemMutation } from '../../../services/ordersApi';
import { getErrorMessage } from '../../../utils/errorHelpers';
import { discountLabel } from '../../../utils/discounts';
import { useAppSelector } from '../../../hooks/redux';
import { useRestaurantTimeZone } from '../../../hooks/useTimeZone';
import { useResendKitchenOrder } from '../../../hooks/useResendKitchenOrder';
import { useIncrementalDailyMenuSend } from '../../../hooks/useIncrementalDailyMenuSend';
import ResendOrderModal from './ResendOrderModal';
import DailyMenuSelectionModal from './DailyMenuSelectionModal';
import { groupSelectionsByCourse } from '../../../utils/dailyMenuGrouping';
import { ProductOrderItem } from '../../../types/products';
import { canCancelOrderItems, canOperateFacturas, isSupportUser } from '../../../utils/roleHelpers';
import { formatOrderNumber } from '../../../utils/orderNumber';

const { Text, Title } = Typography;

interface OrderDetailsModalProps {
  visible: boolean;
  order?: Order;
  onClose: () => void;
  onReprint?: (order: Order) => void;
  onRefresh?: () => void;
}

const OrderDetailsModal: React.FC<OrderDetailsModalProps> = ({ visible, order, onClose, onReprint, onRefresh }) => {
  const { message, modal } = App.useApp();
  const navigate = useNavigate();
  const { user } = useAppSelector((state) => state.auth);
  const { formatInTimeZone } = useRestaurantTimeZone();
  const [showRegenerateFacturaModal, setShowRegenerateFacturaModal] = useState(false);
  const [showFacturaDetalleModal, setShowFacturaDetalleModal] = useState(false);
  const [selectedFacturaId, setSelectedFacturaId] = useState<number | null>(null);
  const [showChangePaymentModal, setShowChangePaymentModal] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState<OrderPayment | null>(null);
  const [expandedRowKeys, setExpandedRowKeys] = useState<React.Key[]>([]);
  const [addCoursesItem, setAddCoursesItem] = useState<OrderItem | null>(null);

  const [timbrarFactura, { isLoading: timbrandoFactura }] = useTimbrarFacturaMutation();
  const [cancelOrderItem, { isLoading: cancellingItem }] = useCancelOrderItemMutation();
  const { send: sendIncrementalDailyMenu } = useIncrementalDailyMenuSend();

  // Fetch fresh order data when modal is visible
  const { data: freshOrder } = useGetOrderQuery(order?.id || '', {
    skip: !visible || !order?.id,
  });

  // Use fresh order data if available, otherwise use prop
  const displayOrder = freshOrder || order;

  useEffect(() => {
    if (!expandedRowKeys.length) return;
    const currentIds = new Set((displayOrder?.orderItems || []).map(i => i.id));
    const pruned = expandedRowKeys.filter(k => currentIds.has(k as string));
    if (pruned.length !== expandedRowKeys.length) setExpandedRowKeys(pruned);
  }, [displayOrder?.orderItems, expandedRowKeys]);

  // Resend kitchen order hook (must run before any early return)
  const orderTypeLabel = displayOrder?.isInternalConsumption
    ? 'Consumo Interno'
    : displayOrder?.type === 'TakeOut' ? 'Para Llevar' : 'Domicilio';
  const {
    resendModalVisible,
    openResendModal,
    closeResendModal,
    handleResendConfirm,
    printing: resending,
  } = useResendKitchenOrder(
    displayOrder,
    `${orderTypeLabel} - ${displayOrder?.customerName || ''}`
  );

  if (!displayOrder) return null;

  const isDeliveryOrTakeOut = displayOrder.type === 'Delivery' || displayOrder.type === 'TakeOut';
  const canReprint = displayOrder.status === 'Closed' && onReprint;
  const canEditOrder = isDeliveryOrTakeOut && displayOrder.status === 'Open';
  const canResendComanda = isDeliveryOrTakeOut && displayOrder.status === 'Open';

  const handleEditOrder = () => {
    const editPath = displayOrder.isInternalConsumption
      ? `/new-order/internal?edit=${displayOrder.id}`
      : displayOrder.type === 'TakeOut'
        ? `/new-order/takeout?edit=${displayOrder.id}`
        : `/new-order/delivery?edit=${displayOrder.id}`;
    onClose();
    navigate(editPath);
  };

  // Cancelar renglones. El backend solo lo rechaza en órdenes divididas o ya
  // cerradas, así que la acción vive aquí y no únicamente en el modal de mesa:
  // Para Llevar / Domicilio / Consumo Interno cuelgan de mesas virtuales, que
  // el mapa de mesas nunca muestra, y hasta ahora no tenían forma de cancelar.
  //
  // Se habilita en cualquier estado NO terminal, no solo en Open: una orden
  // para llevar que ya pasó a Preparing / Ready / Delivered sigue siendo
  // cancelable para el backend, y exigir Open la dejaba otra vez sin salida.
  const normalizedStatus =
    displayOrder.status.charAt(0).toUpperCase() + displayOrder.status.slice(1).toLowerCase();
  const isTerminalStatus = normalizedStatus === 'Closed' || normalizedStatus === 'Cancelled';
  // El gate real está en OrdersController.CancelOrderItem; esto solo evita enseñar un
  // botón que el backend contestaría con 403.
  const canCancelItems =
    !isTerminalStatus && !displayOrder.isSplit && canCancelOrderItems(user?.role);

  const handleCancelItem = async (item: OrderItem) => {
    try {
      await cancelOrderItem({
        orderId: displayOrder.id,
        OrderItemId: item.id,
        Reason: 'Cancelado por el personal',
        Notes: 'Cancelado desde el detalle de la orden',
        // AuthorizedByUserId ya no se manda: el backend toma el autorizador del token.
      }).unwrap();
      message.success('Producto cancelado');
      onRefresh?.();
    } catch (error: unknown) {
      message.error(getErrorMessage(error, 'Error al cancelar el producto'));
    }
  };

  // Destructivo: siempre detrás del menú ⋯ del renglón y con confirmación.
  const confirmCancelItem = (item: OrderItem) => {
    modal.confirm({
      title: `¿Cancelar ${item.quantity}x ${item.dailyMenuName || item.productName}?`,
      content: 'Esta acción no se puede deshacer',
      okText: 'Sí, cancelar',
      okType: 'danger',
      cancelText: 'No',
      onOk: () => handleCancelItem(item),
    });
  };

  // Determinar si se puede regenerar factura. Con la factura cancelada ante el SAT se
  // emite una nueva; si el borrador es una global, el backend lo rechaza con el motivo.
  const puedeOperarFacturas = canOperateFacturas(user?.role, isSupportUser(user?.email));
  const canRegenerateFactura = puedeOperarFacturas && displayOrder.facturaId && (
    displayOrder.facturaEstado === 'Borrador' ||
    displayOrder.facturaEstado === 'Cancelada'
  );

  // Determinar si se puede cambiar método de pago
  const canChangePaymentMethod =
    displayOrder.status === 'Closed' &&
    (user?.role === 'Administrador' || user?.role === 'SuperAdmin' || user?.role === 'Gerente' || user?.role === 'Cajero') &&
    displayOrder.payments && displayOrder.payments.length > 0;

  // Handler para timbrar factura
  const handleTimbrarFactura = async () => {
    if (!displayOrder.facturaId) return;

    try {
      await timbrarFactura(displayOrder.facturaId).unwrap();
      message.success('Factura timbrada exitosamente');
      // Refrescar datos de la orden
      if (onRefresh) {
        onRefresh();
      }
    } catch (error: unknown) {
      console.error('Error al timbrar factura:', error);
      const errorMsg = getErrorMessage(error, 'Error al timbrar la factura');
      message.error(errorMsg, 8);
    }
  };

  // Pie de la tabla: cuando existe la columna de acciones hay que rellenarla
  // con una celda vacía o la fila queda descuadrada.
  const summaryTrailingCell = canCancelItems
    ? <Table.Summary.Cell index={2} />
    : null;

  const getOrderTypeTag = (type: OrderType, isInternalConsumption?: boolean) => {
    if (isInternalConsumption) {
      return <Tag color="purple">🍴 Consumo Interno</Tag>;
    }

    const typeConfig: Record<OrderType, { color: string; text: string; icon: string }> = {
      DineIn: { color: 'blue', text: 'Mesa', icon: '🍽️' },
      TakeOut: { color: 'orange', text: 'Para Llevar', icon: '🥡' },
      Delivery: { color: 'green', text: 'Domicilio', icon: '🛵' },
    };

    const config = typeConfig[type];
    if (!config) {
      return <Tag>{type}</Tag>;
    }
    return (
      <Tag color={config.color}>
        {config.icon} {config.text}
      </Tag>
    );
  };

  const getStatusTag = (status: string) => {
    // Normalize status to PascalCase
    const normalizedStatus = status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();

    const statusConfig: Record<string, { color: string; text: string }> = {
      Open: { color: 'green', text: 'Abierta' },
      Preparing: { color: 'orange', text: 'Preparando' },
      Ready: { color: 'gold', text: 'Lista' },
      Delivered: { color: 'blue', text: 'Entregada' },
      Closed: { color: 'red', text: 'Cerrada' },
      Cancelled: { color: 'volcano', text: 'Cancelada' },
    };

    const config = statusConfig[normalizedStatus];
    if (!config) {
      return <Tag>{status}</Tag>;
    }
    return <Tag color={config.color}>{config.text}</Tag>;
  };

  const itemColumns: ColumnsType<OrderItem> = [
    {
      title: 'Producto',
      dataIndex: 'productName',
      key: 'productName',
      render: (text: string, record: OrderItem) => (
        <div style={{
          backgroundColor: record.isCancelled ? 'var(--bg-error-light)' : undefined,
          padding: record.isCancelled ? '4px 8px' : undefined,
          borderRadius: record.isCancelled ? 4 : undefined,
          marginLeft: record.isCancelled ? -8 : undefined,
          marginRight: record.isCancelled ? -8 : undefined,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Text
              strong
              style={{
                textDecoration: record.isCancelled ? 'line-through' : 'none',
                color: record.isCancelled ? 'var(--color-error-text)' : undefined,
              }}
            >
              {text}
            </Text>
            {record.isCancelled && (
              <Tag color="error" style={{ margin: 0 }}>CANCELADO</Tag>
            )}
          </div>
          {record.variants && record.variants.length > 0 && (
            <div>
              <Text
                type="secondary"
                style={{
                  fontSize: 12,
                  textDecoration: record.isCancelled ? 'line-through' : 'none',
                }}
              >
                {record.variants.map(v => v.variantName).join(', ')}
              </Text>
            </div>
          )}
          {record.notes && (
            <div>
              <Text
                type="secondary"
                italic
                style={{
                  fontSize: 12,
                  textDecoration: record.isCancelled ? 'line-through' : 'none',
                }}
              >
                Nota: {record.notes}
              </Text>
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Cantidad',
      dataIndex: 'quantity',
      key: 'quantity',
      width: 100,
      align: 'center',
      render: (quantity: number, record: OrderItem) => (
        <Text style={{
          textDecoration: record.isCancelled ? 'line-through' : 'none',
          color: record.isCancelled ? 'var(--color-error-text)' : undefined,
        }}>
          {quantity}
        </Text>
      ),
    },
    {
      title: 'Precio Unit.',
      dataIndex: 'unitPrice',
      key: 'unitPrice',
      width: 120,
      align: 'right',
      render: (unitPrice: number, record: OrderItem) => (
        <Text style={{
          textDecoration: record.isCancelled ? 'line-through' : 'none',
          color: record.isCancelled ? 'var(--color-error-text)' : undefined,
        }}>
          ${unitPrice.toFixed(2)}
        </Text>
      ),
    },
    {
      title: 'Subtotal',
      key: 'subtotal',
      width: 120,
      align: 'right',
      render: (_, record: OrderItem) => (
        <Text
          strong
          style={{
            textDecoration: record.isCancelled ? 'line-through' : 'none',
            color: record.isCancelled ? 'var(--color-error-text)' : undefined,
          }}
        >
          ${(record.quantity * record.unitPrice).toFixed(2)}
        </Text>
      ),
    },
    {
      title: 'Despachado',
      dataIndex: 'dispatchedAt',
      key: 'dispatchedAt',
      width: 100,
      align: 'center',
      render: (dispatchedAt: string | undefined, record: OrderItem) => {
        if (record.isCancelled) return <Text type="secondary">—</Text>;
        if (!dispatchedAt) return <Text type="secondary">Pendiente</Text>;
        return (
          <Text style={{ fontSize: 12 }}>
            {formatInTimeZone(dispatchedAt, 'HH:mm')}
          </Text>
        );
      },
    },
    ...(canCancelItems ? [{
      title: '',
      key: 'actions',
      width: 48,
      align: 'center' as const,
      render: (_: unknown, record: OrderItem) => (
        record.isCancelled ? null : (
          <Dropdown
            trigger={['click']}
            disabled={cancellingItem}
            menu={{
              items: [
                {
                  key: 'cancel',
                  danger: true,
                  icon: <DeleteOutlined />,
                  label: 'Cancelar producto',
                  onClick: () => confirmCancelItem(record),
                },
              ],
            }}
          >
            <Button
              type="text"
              size="small"
              icon={<MoreOutlined />}
              aria-label="Opciones del producto"
              onClick={(e) => e.stopPropagation()}
            />
          </Dropdown>
        )
      ),
    }] : []),
  ];

  return (
    <Modal
      title={<Title level={4}>Detalles de la Orden {formatOrderNumber(displayOrder)}</Title>}
      open={visible}
      onCancel={onClose}
      width={900}
      footer={
        (canReprint || canEditOrder || canResendComanda) ? (
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Button onClick={onClose}>Cerrar</Button>
            {canResendComanda && (
              <Button
                icon={<PrinterOutlined />}
                onClick={openResendModal}
                loading={resending}
              >
                Reenviar Comanda
              </Button>
            )}
            {canEditOrder && (
              <Button
                type="primary"
                icon={<EditOutlined />}
                onClick={handleEditOrder}
              >
                Editar Orden
              </Button>
            )}
            {canReprint && (
              <Button
                type="primary"
                icon={<PrinterOutlined />}
                onClick={() => onReprint!(displayOrder)}
              >
                Reimprimir Ticket
              </Button>
            )}
          </div>
        ) : null
      }
    >
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        {/* Order Info */}
        <Descriptions bordered column={2} size="small">
          <Descriptions.Item label="Número de Orden">
            <Text strong style={{ fontSize: 16 }}>
              {formatOrderNumber(displayOrder)}
            </Text>
          </Descriptions.Item>
          <Descriptions.Item label="ID de Orden">
            <Text copyable code style={{ fontSize: 12 }}>
              {displayOrder.id}
            </Text>
          </Descriptions.Item>
          <Descriptions.Item label="Tipo">{getOrderTypeTag(displayOrder.type, displayOrder.isInternalConsumption)}</Descriptions.Item>
          <Descriptions.Item label="Estado">{getStatusTag(displayOrder.status)}</Descriptions.Item>
          <Descriptions.Item label="Mesa">
            <Text strong>{displayOrder.tableName || '-'}</Text>
          </Descriptions.Item>
          <Descriptions.Item label="Mesero Asignado">
            <Text>{displayOrder.assignedWaiterName || '-'}</Text>
          </Descriptions.Item>
          <Descriptions.Item label="Abierta por">
            <Text>{displayOrder.userName}</Text>
          </Descriptions.Item>
          <Descriptions.Item label="Fecha Apertura">
            <Text>{formatInTimeZone(displayOrder.openTime, 'DD/MM/YYYY HH:mm:ss')}</Text>
          </Descriptions.Item>
          {displayOrder.closeTime && (
            <>
              <Descriptions.Item label="Fecha Cierre" span={2}>
                <Text>{formatInTimeZone(displayOrder.closeTime, 'DD/MM/YYYY HH:mm:ss')}</Text>
              </Descriptions.Item>
            </>
          )}
          {(displayOrder.adults !== undefined || displayOrder.children !== undefined) && (
            <Descriptions.Item label="Clientes" span={2}>
              <Space>
                <Text>{displayOrder.adults || 0} Adultos</Text>
                <Divider type="vertical" />
                <Text>{displayOrder.children || 0} Niños</Text>
              </Space>
            </Descriptions.Item>
          )}
        </Descriptions>

        {/* Customer/Delivery Info */}
        {isDeliveryOrTakeOut && (displayOrder.customerName || displayOrder.customerPhone || displayOrder.deliveryAddress) && (
          <div>
            <Title level={5}>
              {displayOrder.type === 'Delivery' ? '🛵 Datos de Entrega' : '🥡 Datos del Cliente'}
            </Title>
            <Alert
              type="info"
              showIcon={false}
              style={{ backgroundColor: 'var(--bg-info-light)', border: '1px solid var(--border-info)' }}
              description={
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {displayOrder.customerName && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <UserOutlined style={{ color: '#1890ff' }} />
                      <Text strong>{displayOrder.customerName}</Text>
                    </div>
                  )}
                  {displayOrder.customerPhone && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <PhoneOutlined style={{ color: '#1890ff' }} />
                      <Text>{displayOrder.customerPhone}</Text>
                    </div>
                  )}
                  {displayOrder.type === 'Delivery' && displayOrder.deliveryAddress && (
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                      <EnvironmentOutlined style={{ color: '#52c41a', marginTop: 4 }} />
                      <Text>{displayOrder.deliveryAddress}</Text>
                    </div>
                  )}
                  {displayOrder.deliveryNotes && (
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginTop: 4 }}>
                      <NotesIcon style={{ color: '#faad14', marginTop: 4 }} />
                      <Text italic type="secondary">Notas: {displayOrder.deliveryNotes}</Text>
                    </div>
                  )}
                </div>
              }
            />
          </div>
        )}

        {/* Order Items */}
        <div>
          <Title level={5}>Items de la Orden</Title>
          <Table
            columns={itemColumns}
            dataSource={displayOrder.orderItems || []}
            rowKey="id"
            pagination={false}
            size="small"
            scroll={{ x: 760 }}
            expandable={{
              rowExpandable: (r: OrderItem) => !!r.dailyMenuId && (r.dailyMenuSelections?.length ?? 0) > 0,
              expandedRowKeys,
              onExpand: (expanded: boolean, record: OrderItem) =>
                setExpandedRowKeys(expanded ? [record.id] : []),
              expandedRowRender: (record: OrderItem) => {
                const courses = groupSelectionsByCourse(record.dailyMenuSelections);
                const selectableCount = record.dailyMenuSelectableCourseCount ?? 0;
                const sentCount = (record.dailyMenuSelections || []).filter(s => s.isSelectable).length;
                const canAddCourses =
                  !record.isCancelled &&
                  displayOrder.status !== 'Closed' &&
                  displayOrder.status !== 'Cancelled' &&
                  selectableCount > 0 &&
                  sentCount < selectableCount;
                return (
                  <div style={{ paddingLeft: 24 }}>
                    {record.dailyMenuName && (
                      <Text strong style={{ display: 'block', marginBottom: 4 }}>
                        {record.dailyMenuName}
                      </Text>
                    )}
                    {courses.map(c => (
                      <div key={c.sortOrder} style={{ marginBottom: 2 }}>
                        <Text strong>{c.courseName}:</Text>{' '}
                        <Text>{c.options.join(', ')}</Text>
                      </div>
                    ))}
                    {canAddCourses && (
                      <Button
                        type="link"
                        size="small"
                        icon={<PlusCircleOutlined />}
                        onClick={() => setAddCoursesItem(record)}
                        style={{ paddingLeft: 0, marginTop: 6 }}
                      >
                        Agregar tiempos faltantes
                      </Button>
                    )}
                  </div>
                );
              },
            }}
            summary={(pageData) => {
              const subtotal = pageData
                .filter(item => !item.isCancelled)
                .reduce(
                  (sum, item) => sum + item.quantity * item.unitPrice,
                  0
                );
              const discountAmount = displayOrder.totalDiscountAmount || 0;
              const total = subtotal - discountAmount;

              return (
                <>
                  <Table.Summary.Row>
                    <Table.Summary.Cell index={0} colSpan={5} align="right">
                      <Text strong>Subtotal:</Text>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={1} align="right">
                      <Text strong>${subtotal.toFixed(2)}</Text>
                    </Table.Summary.Cell>
                    {summaryTrailingCell}
                  </Table.Summary.Row>
                  {discountAmount > 0 && (
                    <Table.Summary.Row>
                      <Table.Summary.Cell index={0} colSpan={5} align="right">
                        <Text type="danger" strong>
                          Descuento:
                        </Text>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={1} align="right">
                        <Text type="danger" strong>
                          -${discountAmount.toFixed(2)}
                        </Text>
                      </Table.Summary.Cell>
                      {summaryTrailingCell}
                    </Table.Summary.Row>
                  )}
                  <Table.Summary.Row>
                    <Table.Summary.Cell index={0} colSpan={5} align="right">
                      <Title level={5} style={{ margin: 0 }}>
                        Total:
                      </Title>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={1} align="right">
                      <Title level={5} style={{ margin: 0 }}>
                        ${total.toFixed(2)}
                      </Title>
                    </Table.Summary.Cell>
                    {summaryTrailingCell}
                  </Table.Summary.Row>
                </>
              );
            }}
          />
        </div>

        {/* Discounts if any */}
        {displayOrder.discounts && displayOrder.discounts.length > 0 && (
          <div>
            <Title level={5}>Descuentos Aplicados</Title>
            {displayOrder.discounts.map((discount, index) => (
              <Tag key={index} color="red">
                {discountLabel(discount, displayOrder.orderItems)}: {discount.type === 'percentage' ? `${discount.value}%` : `$${discount.value}`}
              </Tag>
            ))}
          </div>
        )}

        {/* Payments */}
        {displayOrder.payments && displayOrder.payments.length > 0 && (
          <div>
            <Title level={5}>Pagos Realizados</Title>
            <Descriptions bordered column={1} size="small">
              {displayOrder.payments.map((payment) => {
                const getPaymentIcon = (code: string) => {
                  switch (code.toLowerCase()) {
                    case 'cash':
                    case 'efectivo':
                      return <DollarOutlined style={{ color: '#52c41a' }} />;
                    case 'card':
                    case 'tarjeta':
                      return <CreditCardOutlined style={{ color: '#1890ff' }} />;
                    case 'transfer':
                    case 'transferencia':
                      return <BankOutlined style={{ color: '#722ed1' }} />;
                    default:
                      return <DollarOutlined />;
                  }
                };

                return (
                  <Descriptions.Item
                    key={payment.id}
                    label={
                      <Space>
                        {getPaymentIcon(payment.paymentMethodCode)}
                        <span>{payment.paymentMethodName}</span>
                      </Space>
                    }
                  >
                    <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                      <Space>
                        <Text strong style={{ color: '#52c41a' }}>
                          ${payment.amount.toFixed(2)}
                        </Text>
                        {payment.reference && (
                          <Text type="secondary" style={{ marginLeft: 8 }}>
                            (Ref: {payment.reference})
                          </Text>
                        )}
                      </Space>
                      {canChangePaymentMethod && (
                        <Button
                          type="link"
                          size="small"
                          icon={<SwapOutlined />}
                          onClick={() => {
                            setSelectedPayment(payment);
                            setShowChangePaymentModal(true);
                          }}
                        >
                          Cambiar
                        </Button>
                      )}
                    </Space>
                  </Descriptions.Item>
                );
              })}
              <Descriptions.Item label={<Text strong>Total Pagado</Text>}>
                <Text strong style={{ fontSize: 16, color: '#52c41a' }}>
                  ${displayOrder.payments.reduce((sum, p) => sum + p.amount, 0).toFixed(2)}
                </Text>
              </Descriptions.Item>
            </Descriptions>

            {/* Payment Method Changes History */}
            <div style={{ marginTop: 16 }}>
              <PaymentMethodChangesHistory orderId={displayOrder.id} />
            </div>
          </div>
        )}

        {/* Solicitar Factura - Cuando no tiene factura pero tiene pagos */}
        {!displayOrder.facturaId && displayOrder.payments && displayOrder.payments.length > 0 && displayOrder.status === 'Closed' && (
          <div>
            <Title level={5}>Facturación</Title>
            <Alert
              message="Esta orden no tiene factura"
              description="Puedes generar una factura para esta orden si el cliente lo solicita."
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
            />
            <Button
              block
              type="primary"
              icon={<FileTextOutlined />}
              size="large"
              onClick={() => setShowRegenerateFacturaModal(true)}
            >
              Solicitar Factura
            </Button>
          </div>
        )}

        {/* Factura Information */}
        {displayOrder.facturaId && (
          <div>
            <Title level={5}>Información de Factura</Title>
            <Descriptions bordered column={2} size="small">
              <Descriptions.Item label="Estado">
                {displayOrder.facturaEstado === 'Borrador' && <Tag color="orange">Borrador</Tag>}
                {displayOrder.facturaEstado === 'Timbrada' && <Tag color="green">Timbrada</Tag>}
                {displayOrder.facturaEstado === 'Cancelada' && <Tag color="red">Cancelada</Tag>}
                {displayOrder.facturaEstado === 'CancelacionEnProceso' && <Tag color="gold">Cancelación en proceso</Tag>}
                {!displayOrder.facturaEstado && <Tag>Desconocido</Tag>}
              </Descriptions.Item>
              <Descriptions.Item label="Folio">
                <Text strong>{displayOrder.facturaFolio || '-'}</Text>
              </Descriptions.Item>
              {displayOrder.facturaUUID && (
                <Descriptions.Item label="UUID" span={2}>
                  <Text copyable code style={{ fontSize: 12 }}>
                    {displayOrder.facturaUUID}
                  </Text>
                </Descriptions.Item>
              )}
              {displayOrder.facturaFechaTimbrado && (
                <Descriptions.Item label="Fecha Timbrado" span={2}>
                  <Text>{formatInTimeZone(displayOrder.facturaFechaTimbrado, 'DD/MM/YYYY HH:mm:ss')}</Text>
                </Descriptions.Item>
              )}
            </Descriptions>
            {displayOrder.facturaId && (
              <Space direction="vertical" style={{ marginTop: 16, width: '100%' }} size="middle">
                {/* Botón Ver Detalle - Siempre visible */}
                <Button
                  block
                  type="link"
                  icon={<FileTextOutlined />}
                  onClick={() => {
                    setSelectedFacturaId(displayOrder.facturaId!);
                    setShowFacturaDetalleModal(true);
                  }}
                >
                  Ver Detalle de Factura
                </Button>

                {/* Botón Timbrar - Solo si está en Borrador y no tiene UUID */}
                {puedeOperarFacturas && displayOrder.facturaEstado === 'Borrador' && !displayOrder.facturaUUID && (
                  <Button
                    block
                    type="primary"
                    icon={<SafetyCertificateOutlined />}
                    onClick={handleTimbrarFactura}
                    loading={timbrandoFactura}
                    style={{ backgroundColor: '#52c41a' }}
                  >
                    Timbrar Factura
                  </Button>
                )}

                {/* Botón Regenerar/Generar Nueva */}
                {canRegenerateFactura && (
                  <Button
                    block
                    icon={<ReloadOutlined />}
                    onClick={() => setShowRegenerateFacturaModal(true)}
                  >
                    {displayOrder.facturaEstado === 'Cancelada' ? 'Generar Nueva Factura' : 'Regenerar Factura'}
                  </Button>
                )}

                {/* Botones de Descarga */}
                <Space style={{ width: '100%' }} size="small">
                  <Button
                    icon={<FileTextOutlined />}
                    onClick={() => {
                      window.open(`/api/facturacion/pdf/${displayOrder.facturaId}`, '_blank');
                    }}
                    style={{ flex: 1 }}
                  >
                    Descargar PDF
                  </Button>
                  {displayOrder.facturaUUID && (
                    <Button
                      icon={<FileTextOutlined />}
                      onClick={() => {
                        window.open(`/api/facturacion/xml/${displayOrder.facturaId}`, '_blank');
                      }}
                      style={{ flex: 1 }}
                    >
                      Descargar XML
                    </Button>
                  )}
                </Space>
              </Space>
            )}
          </div>
        )}
      </Space>

      {/* Modal para Regenerar/Crear Factura */}
      <SolicitarFacturaModal
        visible={showRegenerateFacturaModal}
        onCancel={() => setShowRegenerateFacturaModal(false)}
        order={order}
        facturaIdToRegenerate={displayOrder.facturaId || undefined}
        onSuccess={(factura) => {
          setShowRegenerateFacturaModal(false);
          message.success(
            displayOrder.facturaEstado === 'Cancelada'
              ? 'Nueva factura generada exitosamente'
              : displayOrder.facturaId
                ? 'Factura regenerada exitosamente'
                : 'Factura creada exitosamente'
          );
          // Actualizar el facturaId seleccionado para mostrar la nueva factura
          if (factura.facturaId) {
            setSelectedFacturaId(factura.facturaId);
          }
          // Refrescar datos de la orden
          if (onRefresh) {
            onRefresh();
          }
        }}
      />

      {/* Modal de Detalle de Factura */}
      {selectedFacturaId && (
        <FacturaDetalleModal
          facturaId={selectedFacturaId}
          contextOrderId={displayOrder.id}
          visible={showFacturaDetalleModal}
          onClose={() => {
            setShowFacturaDetalleModal(false);
            setSelectedFacturaId(null);
          }}
          onFacturaRegenerated={(newFacturaId) => {
            // Cuando se regenera desde el detalle, actualizar el ID
            setSelectedFacturaId(newFacturaId);
            setShowFacturaDetalleModal(true);
          }}
          onRefresh={() => {
            // Refrescar datos de la orden cuando se timbra o cancela
            if (onRefresh) {
              onRefresh();
            }
          }}
        />
      )}

      {/* Modal de Cambio de Método de Pago */}
      {selectedPayment && (
        <ChangePaymentMethodModal
          visible={showChangePaymentModal}
          orderId={displayOrder.id}
          currentPayment={selectedPayment}
          hasFactura={!!displayOrder.facturaId && displayOrder.facturaEstado === 'Timbrada'}
          onClose={() => {
            setShowChangePaymentModal(false);
            setSelectedPayment(null);
          }}
          onSuccess={() => {
            // Refrescar datos de la orden
            if (onRefresh) {
              onRefresh();
            }
          }}
        />
      )}

      <ResendOrderModal
        visible={resendModalVisible}
        onCancel={closeResendModal}
        onConfirm={handleResendConfirm}
        orderId={displayOrder.id}
      />

      {addCoursesItem && (
        <DailyMenuSelectionModal
          visible={!!addCoursesItem}
          onCancel={() => setAddCoursesItem(null)}
          onAddItem={async (item: ProductOrderItem) => {
            const newSelections = (item.dailyMenuSelections || [])
              .filter(s => !!s.optionId)
              .map(s => ({ courseId: s.courseId, optionId: s.optionId! }));
            const customerLabel = displayOrder.customerName?.trim() || '';
            const tableLabel = displayOrder.type === 'TakeOut'
              ? (customerLabel ? `Para Llevar - ${customerLabel}` : 'Para Llevar')
              : displayOrder.type === 'Delivery'
                ? (customerLabel ? `Domicilio - ${customerLabel}` : 'Domicilio')
                : displayOrder.tableNumber
                  ? `Mesa ${displayOrder.tableNumber}`
                  : '';
            const ok = await sendIncrementalDailyMenu({
              order: displayOrder,
              orderItem: addCoursesItem,
              newSelections,
              tableLabel,
            });
            if (ok) {
              setAddCoursesItem(null);
              if (onRefresh) onRefresh();
            }
          }}
          initialValues={{
            dailyMenuId: addCoursesItem.dailyMenuId!,
            quantity: 1,
            notes: addCoursesItem.notes,
            dailyMenuSelections: (addCoursesItem.dailyMenuSelections || []).map(s => ({
              courseId: s.dailyMenuCourseId,
              courseName: s.courseName,
              optionId: s.dailyMenuOptionId,
              optionName: s.optionName,
              priceModifier: s.priceModifier,
              sortOrder: s.courseSortOrder,
            })),
          }}
          sentCourseIds={(addCoursesItem.dailyMenuSelections || [])
            .filter(s => s.isSelectable)
            .map(s => s.dailyMenuCourseId)}
        />
      )}
    </Modal>
  );
};

export default OrderDetailsModal;
